import React from 'react';
import { Combobox } from '@base-ui/react/combobox';
import styles from './CampoSeletor.module.css';
import Icon from '@mdi/react';
import { mdiChevronDown, mdiClose, mdiLoading } from '@mdi/js';

export type CampoSeletorAsyncOption = {
    id: string;
    label: string;
};

type Props = {
    label?: string;
    placeholder?: string;
    /** Texto atualmente digitado no campo de busca */
    inputValue: string;
    /** Chamado quando o usuário digita — use para debounce + fetch */
    onInputChange: (text: string) => void;
    /** ID da opção selecionada (e.g., número do ASN como string) */
    selectedId: string | null;
    /** Chamado quando o usuário escolhe uma opção */
    onSelect: (id: string, label: string) => void;
    /** Chamado quando o usuário limpa a seleção */
    onClear: () => void;
    options: CampoSeletorAsyncOption[];
    loading?: boolean;
    disabled?: boolean;
    error?: string;
    hint?: string;
};

export default function CampoSeletorAsync({
    label,
    placeholder,
    inputValue,
    onInputChange,
    selectedId,
    onSelect,
    onClear,
    options,
    loading,
    disabled,
    error,
    hint,
}: Props) {
    const handleValueChange = (id: string | null) => {
        if (!id) {
            onClear();
            return;
        }
        const opt = options.find((o) => o.id === id);
        onSelect(id, opt?.label ?? id);
    };

    const handleInputValueChange = (text: string) => {
        // Quando o usuário digita (não seleciona), repassa para o pai
        onInputChange(text);
    };

    return (
        <div style={{ width: '100%' }}>
            {label && <label className={styles.label}>{label}</label>}
            <Combobox.Root
                value={selectedId ?? ''}
                onValueChange={handleValueChange}
                inputValue={inputValue}
                onInputValueChange={handleInputValueChange}
                disabled={disabled}
            >
                <div
                    className={[
                        styles.container,
                        error ? styles.error : null,
                        disabled ? styles.disabled : null,
                    ]
                        .filter(Boolean)
                        .join(' ')}
                >
                    <Combobox.Input
                        placeholder={placeholder}
                        style={{
                            flex: 1,
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            color: 'inherit',
                            font: 'inherit',
                            minWidth: 0,
                            padding: 0,
                        }}
                    />
                    {selectedId && !disabled && (
                        <span
                            onClick={(e) => { e.stopPropagation(); onClear(); }}
                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px' }}
                        >
                            <Icon path={mdiClose} size="20px" />
                        </span>
                    )}
                    {loading
                        ? <Icon path={mdiLoading} spin size={1} style={{ opacity: 0.5 }} />
                        : <Combobox.Icon className={styles.arrow}><Icon path={mdiChevronDown} size={1} /></Combobox.Icon>
                    }
                </div>
                <Combobox.Portal>
                    <Combobox.Positioner side="bottom" align="start" sideOffset={4} className={styles.positioner}>
                        <Combobox.Popup className={styles.options}>
                            <Combobox.List>
                                {options.map((opt) => (
                                    <Combobox.Item key={opt.id} value={opt.id} className={styles.option}>
                                        {opt.label}
                                    </Combobox.Item>
                                ))}
                            </Combobox.List>
                            {!loading && options.length === 0 && (
                                <Combobox.Empty style={{ padding: '8px', fontSize: '0.875rem', opacity: 0.6 }}>
                                    {inputValue.trim().length < 2
                                        ? 'Digite para buscar um ASN...'
                                        : 'Nenhum resultado encontrado'}
                                </Combobox.Empty>
                            )}
                        </Combobox.Popup>
                    </Combobox.Positioner>
                </Combobox.Portal>
            </Combobox.Root>
            {hint && !error && <div className={styles.hint}>{hint}</div>}
            {error && <div className={styles.errorMsg}>{error}</div>}
        </div>
    );
}
